<template>
  <button
    :class="{
      [`button-${variant}`]: true,
      [`button-${size}`]: true,
      'button-loading': loading,
      'button-icon': icon && !loading
    }"
    :disabled="loading || disabled"
  >
    <span
      v-if="loading"
      class="material-symbols-outlined rotate"
    >
      progress_activity
    </span>
    <template v-else>
      <span
        v-if="icon"
        class="material-symbols-outlined"
        >{{ icon }}</span
      >
      <span>{{ text }}</span>
    </template>
  </button>
</template>
<script lang="ts" setup>
//type is a fallthrough attibute
const {
  loading = false,
  disabled = false,
  variant = 'primary',
  size = 'lg',
  text,
  icon
} = defineProps<{
  text: string;
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'secondary-on-background'
    | 'destructive'
    | 'primary-link'
    | 'secondary-link';
  size?: 'xs' | 'sm' | 'md' | 'lg';
}>();
</script>
<style lang="scss" scoped>
[class|='button'] {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $border-radius-md;
  font-weight: $font-weight-bold;
  gap: $spacing-4xs;
}

.button-primary {
  border: none;
  background-color: $brand-blue;
  border: 1px solid $brand-blue;
  color: $white;
  &:hover {
    background-color: $blue-46;
    border: 1px solid $blue-46;
  }
  &:active {
    background-color: $blue-66;
    border: 1px solid $blue-66;
  }
  &:disabled {
    background-color: $gray-48;
    border: 1px solid $gray-48;
  }
  &:focus {
    outline: 2px solid $blue-66;
  }
}

.button.button-primary.button-loading {
  background-color: $gray-48;
}

.button-secondary {
  border: 1px solid $brand-blue;
  color: $brand-blue;
  background-color: $white;
  &:hover {
    border-color: $blue-36;
    outline: 1px solid $blue-36;
  }
  &:active {
    border-color: $blue-66;
    outline: 1px solid $blue-66;
  }
  &:disabled {
    outline: none;
    border: 1px solid $gray-48;
    color: $gray-48;
  }
  &:focus {
    border-color: $blue-66;
    outline: 1px solid $blue-66;
  }
}

.button-tertiary {
  border: 1px solid $brand-black;
  color: $brand-black;
  background-color: transparent;
  &:hover {
    border-color: $gray-68;
    outline: 1px solid $gray-68;
  }
  &:active {
    border-color: $gray-78;
    outline: 1px solid $gray-78;
  }
  &:disabled {
    outline: none;
    border: 1px solid $gray-48;
    color: $gray-48;
  }
  &:focus {
    border-color: $gray-78;
    outline: 1px solid $gray-78;
  }
}
.button-destructive {
  border: 1px solid $red-44;
  background-color: $white;
  color: $red-44;
  &:hover {
    border-color: $red-34;
    outline: 1px solid $red-34;
  }
  &:active {
    outline: 1px solid $red-44;
  }
  &:disabled {
    outline: none;
    border: 1px solid $gray-48;
    color: $gray-48;
  }
  &:focus {
    border-color: $blue-66;
    outline: 1px solid $blue-66;
  }
}

.button-secondary-on-background {
  border: 1px solid $white;
  background-color: transparent;
  color: $white;
  &:hover {
    outline: 1px solid $white;
  }
  &:active {
    outline: 1px solid $white;
  }
  &:focus {
    outline: 1px solid $white;
  }
}

.button.button-secondary-on-background.button-loading,
.button-destructive.button-loading,
.button-secondary.button-loading {
  border-color: $gray-48;
  outline: 1px solid $gray-48;
  color: $gray-48;
}

.button-xs {
  padding: $spacing-3xs $spacing-xs;
  font-size: $font-size-2xs;
  gap: 0;
  .material-symbols-outlined {
    font-size: $font-size-sm;
  }
}
.button-xs.button-icon {
  padding: $spacing-3xs $spacing-sm $spacing-3xs $spacing-3xs; //accommodate icon white space
}

.button-sm {
  padding: $spacing-xs $spacing-md;
  font-size: $font-size-xs;
  gap: $spacing-4xs;
  .material-symbols-outlined {
    font-size: $font-size-base;
  }
}
.button-sm.button-icon {
  padding: $spacing-xs $spacing-sm $spacing-xs $spacing-xs; //accommodate icon white space
}

.button-md {
  padding: $spacing-md $spacing-lg;
  font-size: $font-size-sm;
  gap: 0;
  .material-symbols-outlined {
    font-size: $font-size-lg;
  }
}
.button-md.button-icon {
  padding: $spacing-md $spacing-lg $spacing-md $spacing-sm; //accommodate icon white space
}

.button-lg {
  padding: $spacing-md $spacing-xl;
  font-size: $font-size-base;
}
.button-lg.button-icon {
  padding: $spacing-md $spacing-xl $spacing-md $spacing-lg; //accommodate icon white space
}

.button-primary-link,
.button-secondary-link {
  background-color: transparent;
  border: none;
}

.button-primary-link {
  color: $brand-blue;
  &:hover {
    color: $blue-46;
  }
}
.button-secondary-link {
  color: $brand-black;
  &:hover {
    color: $gray-78;
  }
}
.button-primary-link.button-xs,
.button-secondary-link.button-xs,
.button-primary-link.button-icon.button-xs,
.button-secondary-link.button-icon.button-xs {
  padding: 0;
  gap: $spacing-4xs;
  font-size: $font-size-2xs;
  .material-symbols-outlined {
    font-size: $font-size-xl;
  }
}

.button-primary-link.button-sm,
.button-secondary-link.button-sm,
.button-primary-link.button-icon.button-sm,
.button-secondary-link.button-icon.button-sm {
  padding: 0;
  gap: $spacing-4xs;
  font-size: $font-size-xs;
  .material-symbols-outlined {
    font-size: $font-size-xl;
  }
}
.button-primary-link.button-md,
.button-secondary-link.button-md,
.button-primary-link.button-icon.button-md,
.button-secondary-link.button-icon.button-md {
  padding: 0;
  gap: $spacing-4xs;
  .material-symbols-outlined {
    font-size: $font-size-xl;
  }
}
.button-primary-link.button-lg,
.button-secondary-link.button-lg,
.button-primary-link.button-icon.button-lg,
.button-secondary-link.button-icon.button-lg {
  padding: 0;
  gap: $spacing-4xs;
  .material-symbols-outlined {
    font-size: $font-size-xl;
  }
}
</style>
